import '@/assets/css/main.css';

import { createApp, defineAsyncComponent } from 'vue';
import { createPinia } from 'pinia';

import GlobalWebsiteApp from '@/apps/website/GlobalWebsiteApp.vue';

import CustomerPortalApp from '@/apps/website/customer-portal/CustomerPortalApp.vue';
import CustomerPortalRouter from '@/apps/website/customer-portal/router';

// Synchronous imports for always-used components
import TemperatureConversionCalculatorApp from '@/apps/website/calculators/TemperatureConversionCalculatorApp.vue';
import ThreePhaseUnitCalculatorApp from '@/apps/website/calculators/ThreePhaseUnitCalculatorApp.vue';
import SinglePhaseUnitCalculatorApp from '@/apps/website/calculators/SinglePhaseUnitCalculatorApp.vue';
import PowerFlowrateTempCalculatorApp from '@/apps/website/calculators/PowerFlowrateTempCalculatorApp.vue';
import OhmsLawCalculatorApp from '@/apps/website/calculators/OhmsLawCalculatorApp.vue';
import HeatTransferThroughConvectionCalculatorApp from '@/apps/website/calculators/HeatTransferThroughConvectionCalculatorApp.vue';
import ACFMtoSCFMCalculatorApp from '@/apps/website/calculators/ACFMtoSCFMCalculatorApp.vue';
import MassFlowToVolumetricFlowCalculatorApp from '@/apps/website/calculators/MassFlowToVolumetricFlowCalculatorApp.vue';
import StandardFlowrateCalculatorApp from '@/apps/website/calculators/StandardFlowrateCalculatorApp.vue';
import PressureConversionCalculatorApp from '@/apps/website/calculators/PressureConversionCalculatorApp.vue';

const globalApp = createApp(GlobalWebsiteApp);

const pinia = createPinia();

globalApp.use(pinia);

globalApp.mount('#globalWebsiteAppContainer');

// Keep these async since they might not always be used
const WebsiteSearchApp = defineAsyncComponent(() => import('@/apps/website/website-search/WebsiteSearchApp.vue'));
const UserAuthApp = defineAsyncComponent(() => import('@/apps/website/user-auth/UserAuthApp.vue'));

const FarnamFlexSpecsApp = defineAsyncComponent(() => import('@/apps/website/farnam-custom/FlexSpecsApp.vue'));
const FarnamBuildYourOwnApp = defineAsyncComponent(() => import('@/apps/website/farnam-custom/BuildYourOwnApp.vue'));
const FarnamContactFormApp = defineAsyncComponent(() => import('@/apps/website/farnam-custom/ContactFormApp.vue'));
const FarnamPowerFlowRateApp = defineAsyncComponent(() => import('@/apps/website/farnam-custom/PowerFlowRateApp.vue'));

const DuctContactFormApp = defineAsyncComponent(() => import('@/apps/website/forms/DuctContactFormApp.vue'));

const HiTempCartridgeHeaterApp = defineAsyncComponent(() => import('@/apps/website/heaters/HiTempCartridgeHeaterApp.vue'));

const constructorsApps = [
  {
    elementId: 'website-search',
    component: WebsiteSearchApp,
  },
  {
    elementId: 'user-auth-app',
    component: UserAuthApp,
  },
  {
    elementId: 'temperature-conversion-calculator-app',
    component: TemperatureConversionCalculatorApp,
  },
  {
    elementId: 'three-phase-unit-calculator-app',
    component: ThreePhaseUnitCalculatorApp,
  },
  {
    elementId: 'single-phase-unit-calculator-app',
    component: SinglePhaseUnitCalculatorApp,
  },
  {
    elementId: 'power-flowrate-temp-calculator-app',
    component: PowerFlowrateTempCalculatorApp,
  },
  {
    elementId: 'ohms-law-calculator-app',
    component: OhmsLawCalculatorApp,
  },
  {
    elementId: 'heat-transfer-through-convection-calculator-app',
    component: HeatTransferThroughConvectionCalculatorApp,
  },
  {
    elementId: 'acfm-to-scfm-calculator-app',
    component: ACFMtoSCFMCalculatorApp,
  },
  {
    elementId: 'mass-flow-to-volumetric-flow-calculator-app',
    component: MassFlowToVolumetricFlowCalculatorApp,
  },
  {
    elementId: 'standard-flowrate-calculator-app',
    component: StandardFlowrateCalculatorApp,
  },
  {
    elementId: 'pressure-conversion-calculator-app',
    component: PressureConversionCalculatorApp,
  },
  {
    elementId: 'farnam-flex-specs-app',
    component: FarnamFlexSpecsApp,
  },
  {
    elementId: 'farnam-build-your-own-app',
    component: FarnamBuildYourOwnApp,
  },
  {
    elementId: 'farnam-contact-form-app',
    component: FarnamContactFormApp,
  },
  {
    elementId: 'farnam-power-flow-rate-app',
    component: FarnamPowerFlowRateApp,
  },
  {
    elementId: 'duct-contact-form-app',
    component: DuctContactFormApp,
  },
  {
    elementId: 'hi-temp-cartridge-heater-app',
    component: HiTempCartridgeHeaterApp,
  },
];

const constructors = document.querySelectorAll('.complex_component');
constructors.forEach((element: HTMLElement) => {
  constructorsApps.forEach((app) => {
    if (element.dataset.component_id === app.elementId) {
      createApp(app.component).use(pinia).mount(`#${element.id}[data-component_id="${app.elementId}"]`);
    }
  });
});

if (document.getElementById('customer-portal-app-container')) {
  createApp(CustomerPortalApp).use(pinia).use(CustomerPortalRouter).mount('#customer-portal-app-container');
}
